import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { publicDocumentApi } from '@/lib/api';
import { DocumentNotFound } from './DocumentNotFound';

/**
 * LegacyHashRedirect handles redirects from old lnk.invoiceowl.com short URLs.
 *
 * When users access /:legacyHash (e.g., /ZO5s0mOn), this component:
 * 1. Calls the API to look up the hash
 * 2. Redirects to the appropriate document page (/invoice/:id or /quote/:id)
 * 3. Shows a "not found" page if the hash doesn't exist
 *
 * Hash format: 6-16 char alphanumeric (e.g., ZO5s0mOn)
 * Distinguished from business IDs by format (bus_ prefix)
 */
export function LegacyHashRedirect() {
  const { legacyHash } = useParams<{ legacyHash: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function lookupHash() {
      if (!legacyHash) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      // Business IDs have bus_ prefix - redirect to dashboard
      // This handles the edge case where someone navigates to /bus_xxx without a sub-path
      if (legacyHash.startsWith('bus_')) {
        navigate(`/${legacyHash}/dashboard`, { replace: true });
        return;
      }

      // Check if this looks like a legacy hash (alphanumeric, 6-16 chars)
      const isLegacyHash = /^[A-Za-z0-9_-]{6,16}$/.test(legacyHash);

      if (!isLegacyHash) {
        // Not a legacy hash format - show not found
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await publicDocumentApi.lookupLegacyHash(legacyHash);

        if (response.data?.redirect_url) {
          // Navigate to the document page
          navigate(response.data.redirect_url, { replace: true });
        } else {
          setNotFound(true);
        }
      } catch {
        // Hash not found or API error
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    }

    lookupHash();
  }, [legacyHash, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-600">Looking up document...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (notFound) {
    return <DocumentNotFound documentType="Invoice" />;
  }

  // This shouldn't render - we either redirect or show not found
  return null;
}
